import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CountUp } from '@/components/roi/CountUp';
import { MarketEvidenceField } from '@/components/pitch-v5/MarketEvidenceField';
import { revealFrom } from '@/lib/motion';
import { ScaleBar } from './ScaleBar';
import { SectionShell } from './SectionShell';

/**
 * Market signal — Volume + compliance burden.
 *
 * Current reference figures, each scoped to its published geography and
 * period. Designed to land the scale of the opportunity in a single glance:
 *   23,938 insolvencies. 7,460 bankruptcies. 12,579 MVLs.
 *   1,262 appointment takers.
 *
 * Sources: Insolvency Service annual summary for calendar year 2025;
 * Companies House FYE 2025 register activity; Insolvency Service 2025
 * practitioner-regulation review (counts as at 1 January 2026).
 */

interface InsolvencyRow {
  label: string;
  /** 2025 value — the only value rendered in the static rows. */
  value: number;
  /**
   * 2019 comparator. The focused V1 rows use the Q4 2019 release values;
   * the legacy V5 rows retain the revised December 2025 long-run values.
   */
  previousValue: number;
}

const INSOLVENT_BREAKDOWN: InsolvencyRow[] = [
  { label: 'CVLs', value: 18525, previousValue: 12056 },
  { label: 'Compulsory liquidations', value: 3730, previousValue: 2942 },
  { label: 'Administrations', value: 1495, previousValue: 1816 },
  { label: 'CVAs', value: 186, previousValue: 355 },
];

const FOCUSED_INSOLVENT_COMPARISON: InsolvencyRow[] = [
  { label: 'CVLs', value: 18525, previousValue: 12060 },
  { label: 'Compulsory liquidations', value: 3730, previousValue: 2970 },
];

/**
 * England & Wales registered company insolvency annual totals from the latest
 * official long-run series. 2019 is the last full pre-pandemic year. Passed
 * explicitly to the V5 evidence field for its comparison readout; never used
 * as a bar denominator.
 */
const TOTAL_INSOLVENCIES_PREVIOUS = 17170;
const TOTAL_INSOLVENCIES_CURRENT = 23938;

/** Bars in the breakdown are drawn against its largest category. */
const BREAKDOWN_SCALE = Math.max(
  ...INSOLVENT_BREAKDOWN.map((row) => row.value),
);

const AI_MARKET_SIGNALS = [
  {
    title: 'AI adoption is spreading across insolvency firms',
    body: 'Numerous reports, including R3’s survey, show growing adoption across document digitisation, spreadsheet workflows, data analysis, document review and investigations. The timing is right for a governed product built specifically for insolvency work',
  },
  {
    title: 'A revolutionary alternative to legacy based systems',
    body: 'Asset Hound is built AI first around workflow, evidence and human approval',
  },
];

export function MarketSignal({
  interactiveGraphic = false,
  focusedComparison = false,
}: {
  /**
   * V5-only opt-in: replaces the static breakdown list with the evidence
   * field, whose category rows are the selection buttons. Off by default so
   * every other page keeps the existing static list.
   */
  interactiveGraphic?: boolean;
  /** V1-only 2019/2025 comparison requested for the investor run-through. */
  focusedComparison?: boolean;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const reducedMotion = useReducedMotion() ?? false;
  const rows = INSOLVENT_BREAKDOWN;

  const breakdownList = (
    <ul className="mt-8 grid grid-cols-1 gap-x-8 gap-y-5 border-t border-border-subtle pt-6 text-sm sm:grid-cols-2">
      {rows.map((row, i) => (
        <BreakdownRow
          key={row.label}
          row={row}
          index={i}
          scale={BREAKDOWN_SCALE}
        />
      ))}
    </ul>
  );

  return (
    <SectionShell
      id="signal"
      index={1}
      eyebrow="Market signal"
      title={
        <>
          Sustained insolvency volume{' '}
          <span className="text-brand-bright">in 2025</span>
        </>
      }
      intro="England & Wales recorded 23,938 company insolvencies in 2025, remaining near historic highs. At the same time, AI has moved from experiment to practitioner tooling, and the timing is right for an AI native alternative to manual workflow and legacy case systems"
    >
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_0.8fr]">
        {/* Primary stat: England & Wales company insolvencies 2025 */}
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className={[
            'pitch-card',
            'market-signal-primary',
            interactiveGraphic ? 'market-signal-primary--interactive' : '',
            'relative isolate overflow-hidden rounded-2xl p-8 sm:p-10',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute -right-32 -top-32 -z-10 h-80 w-80 rounded-full opacity-[0.16] blur-[100px]"
            style={{ background: '#3B82F6' }}
          />
          <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-dim">
            England &amp; Wales company insolvencies · 2025
          </span>
          <div className="mt-5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="font-semibold tabular-nums text-ink-strong [font-size:64px] [line-height:0.95] sm:[font-size:88px]">
              <CountUp
                target={23938}
                duration={2}
                delay={0.2}
                formatter={(n) => Math.round(n).toLocaleString('en-GB')}
              />
            </span>
            <span className="text-sm font-medium text-ink-muted">
              insolvent cases
            </span>
          </div>

          {interactiveGraphic ? (
            focusedComparison ? (
              <div className="pitch-v5 market-evidence-v1-bridge">
                <div className="market-signal-evidence-layout">
                  <MarketEvidenceField
                    data={rows}
                    comparisonData={FOCUSED_INSOLVENT_COMPARISON}
                    activeIndex={activeIndex}
                    onActivateIndex={setActiveIndex}
                    totalPrevious={17196}
                    totalCurrent={TOTAL_INSOLVENCIES_CURRENT}
                    reducedMotion={reducedMotion}
                    showAnnualComparison={false}
                  />
                </div>
              </div>
            ) : (
              <div className="market-signal-evidence-layout">
                <MarketEvidenceField
                  data={rows}
                  activeIndex={activeIndex}
                  onActivateIndex={setActiveIndex}
                  totalPrevious={TOTAL_INSOLVENCIES_PREVIOUS}
                  totalCurrent={TOTAL_INSOLVENCIES_CURRENT}
                  reducedMotion={reducedMotion}
                />
              </div>
            )
          ) : (
            breakdownList
          )}
        </motion.article>

        {/* Secondary: individual cases + solvent wind-down + practitioners */}
        <div className="flex flex-col gap-4">
          <SecondaryStat
            eyebrow="Bankruptcies · England & Wales · 2025"
            value={7460}
            note="Individual bankruptcy cases recorded during the calendar year"
            delay={0.1}
          />
          <SecondaryStat
            eyebrow="Members’ voluntary liquidations · UK · FYE 2025"
            value={12579}
            note="A separate Companies House measure of solvent liquidation volume"
            delay={0.2}
          />
          <SecondaryStat
            eyebrow="Active appointment takers"
            value={1262}
            note="A concentrated practitioner market carrying sustained case volume"
            delay={0.3}
          />
        </div>
      </div>

      <div className="pitch-strip mt-6 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border-subtle lg:grid-cols-2">
        {AI_MARKET_SIGNALS.map((signal, index) => (
          <motion.article
            key={signal.title}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
              duration: 0.5,
              ease: [0.16, 1, 0.3, 1],
              delay: 0.1 + index * 0.07,
            }}
            className="pitch-cell flex flex-col gap-3 p-6 sm:p-7"
          >
            <h3 className="text-[17px] font-semibold leading-snug text-ink-strong">
              {signal.title}
            </h3>
            <p className="text-[13px] leading-relaxed text-ink-muted">
              {signal.body}
            </p>
          </motion.article>
        ))}
      </div>

      <p className="mt-5 text-[11px] leading-relaxed text-ink-dim">
        Sources:{' '}
        <a
          href="https://www.gov.uk/government/statistics/company-insolvencies-december-2025"
          target="_blank"
          rel="noreferrer"
          className="underline decoration-border-strong underline-offset-2 hover:text-ink-muted"
        >
          Insolvency Service company statistics
        </a>
        {focusedComparison ? (
          <span data-focused-comparison-context>
            {' · '}
            <a
              href="https://www.gov.uk/government/statistics/company-insolvency-statistics-october-to-december-2019"
              target="_blank"
              rel="noreferrer"
              className="underline decoration-border-strong underline-offset-2 hover:text-ink-muted"
            >
              Insolvency Service 2019 company statistics
            </a>
          </span>
        ) : null}
        {' · '}
        <a
          href="https://www.gov.uk/government/statistics/individual-insolvencies-december-2025"
          target="_blank"
          rel="noreferrer"
          className="underline decoration-border-strong underline-offset-2 hover:text-ink-muted"
        >
          Insolvency Service individual statistics
        </a>
        {' · '}
        <a
          href="https://www.gov.uk/government/publications/insolvency-practitioner-regulation-process-review-2025/annual-review-of-insolvency-practitioner-regulation-2025"
          target="_blank"
          rel="noreferrer"
          className="underline decoration-border-strong underline-offset-2 hover:text-ink-muted"
        >
          IP regulation review
        </a>
        {' · '}
        <a
          href="https://www.gov.uk/government/statistics/companies-register-activities-statistical-release-april-2024-to-march-2025/companies-register-activities-april-2024-to-march-2025"
          target="_blank"
          rel="noreferrer"
          className="underline decoration-border-strong underline-offset-2 hover:text-ink-muted"
        >
          Companies House FYE 2025
        </a>
        {' · '}
        <a
          href="https://www.r3.org.uk/press-policy-and-research/r3-blog/more/32748/page/1/making-your-views-count/"
          target="_blank"
          rel="noreferrer"
          className="underline decoration-border-strong underline-offset-2 hover:text-ink-muted"
        >
          R3 member survey update
        </a>
        {' · '}
        <a
          href="https://www.r3.org.uk/technical-library/recovery/recovery-news/more/32567/page/1/ai-for-insolvency-professionals-how-to-get-started-safely-ethically-and-effectively/"
          target="_blank"
          rel="noreferrer"
          className="underline decoration-border-strong underline-offset-2 hover:text-ink-muted"
        >
          R3 article on AI adoption
        </a>
        {focusedComparison ? (
          <span className="mt-2 block" data-focused-comparison-context>
            Comparison basis: England &amp; Wales calendar-year, seasonally
            adjusted procedure counts as published in each release
          </span>
        ) : null}
      </p>
    </SectionShell>
  );
}

function BreakdownRow({
  row,
  index,
  scale,
}: {
  row: InsolvencyRow;
  index: number;
  /** Largest value in the group; every bar is drawn against it. */
  scale: number;
}) {
  const delay = 0.4 + index * 0.08;

  return (
    <motion.li
      variants={revealFrom(-8, 0.4)}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.5 }}
      transition={{ delay }}
      className="flex flex-col gap-2"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-ink-muted">{row.label}</span>
        <span className="font-medium tabular-nums text-brand-bright">
          <CountUp
            target={row.value}
            duration={1.2}
            delay={0.5 + index * 0.08}
            formatter={(n) => Math.round(n).toLocaleString('en-GB')}
          />
        </span>
      </div>
      {/* Scaled against the largest row rather than the section total: the
          four categories don't sum to 23,938, so a share-of-total bar
          would assert an accounting relationship the figures don't have. */}
      <ScaleBar to={row.value / scale} delay={delay + 0.1} />
    </motion.li>
  );
}

function SecondaryStat({
  eyebrow,
  value,
  note,
  delay,
}: {
  eyebrow: string;
  value: number;
  note: React.ReactNode;
  delay: number;
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay }}
      className="pitch-card flex flex-col gap-3 rounded-2xl p-6 sm:p-7"
    >
      <span className="text-[11px] font-medium uppercase tracking-[0.22em] text-ink-dim">
        {eyebrow}
      </span>
      <span className="font-semibold tabular-nums text-ink-strong [font-size:44px] [line-height:0.95] sm:[font-size:52px]">
        <CountUp
          target={value}
          duration={1.6}
          delay={delay + 0.2}
          formatter={(n) => Math.round(n).toLocaleString('en-GB')}
        />
      </span>
      <p className="text-[13px] leading-relaxed text-ink-muted">{note}</p>
    </motion.article>
  );
}
